import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("vd_cart") || "[]");
        } catch {
            return [];
        }
    });
    const [cartOpen, setCartOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem("vd_cart", JSON.stringify(items));
    }, [items]);

    const addItem = useCallback((product, size) => {
        setItems((prev) => {
            const key = `${product.id}_${size}`;
            const existing = prev.find((i) => i.key === key);
            if (existing) {
                return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + 1, 10) } : i));
            }
            return [
                ...prev,
                { key, productId: product.id, name: product.name, price: product.price, image: product.image, size, qty: 1 },
            ];
        });
        setCartOpen(true);
    }, []);

    const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);

    const updateQty = useCallback(
        (key, delta) =>
            setItems((prev) =>
                prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, Math.min(10, i.qty + delta)) } : i)),
            ),
        [],
    );

    const clearCart = useCallback(() => setItems([]), []);

    const { count, total } = useMemo(
        () => ({
            count: items.reduce((a, i) => a + i.qty, 0),
            total: items.reduce((a, i) => a + i.qty * i.price, 0),
        }),
        [items],
    );

    return (
        <CartContext.Provider
            value={{ items, addItem, removeItem, updateQty, clearCart, count, total, cartOpen, setCartOpen }}
        >
            {children}
        </CartContext.Provider>
    );
};
